import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
	formatDay,
	formatTime,
	groupStartsByDay,
	monthWeeks,
	monthWindow,
	timezoneChoices,
	timezoneLabel,
	ymdInZone,
} from '../../lib/bookingTimes';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MAX_AUTO_MOVES = 3;
/** The accent color is a CSS variable, which Tailwind's `/10` opacity can't tint. */
const OPEN_TINT = 'bg-[color-mix(in_srgb,var(--color-portal-accent,#2563eb)_12%,transparent)] hover:bg-[color-mix(in_srgb,var(--color-portal-accent,#2563eb)_22%,transparent)]';

/** PD-17646 — a month of open days, the chosen day's times, and the guest's time zone. */
export function BookingTimePicker({
	queryKey,
	fetchTimes,
	hostTimezone,
	timezone,
	onTimezoneChange,
	selected,
	onSelect,
}: {
	/** Separates this page's times in the query cache. */
	queryKey: ReadonlyArray<string | null>;
	fetchTimes: (window: { from: string; to: string }) => Promise<Array<string>>;
	hostTimezone: string;
	timezone: string;
	onTimezoneChange: (timezone: string) => void;
	selected: string | null;
	onSelect: (start: string) => void;
}): JSX.Element {
	const { t } = useTranslation();
	const browserZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
	const today = useMemo(() => ymdInZone(new Date(), timezone), [timezone]);
	const [year, setYear] = useState(() => Number(today.slice(0, 4)));
	const [month, setMonth] = useState(() => Number(today.slice(5, 7)));
	const [chosenDay, setChosenDay] = useState<string | null>(null);
	const autoMoves = useRef(0);

	const { data: starts, isLoading, isError } = useQuery({
		queryKey: ['booking-times', ...queryKey, year, month],
		queryFn: () => fetchTimes(monthWindow(year, month)),
		// A time can be taken by someone else, so these are never kept for long.
		staleTime: 30_000,
	});

	const days = useMemo(() => groupStartsByDay(starts ?? [], timezone), [starts, timezone]);
	const monthPrefix = `${String(year)}-${String(month).padStart(2, '0')}`;
	const firstOpenDay = [...days.keys()].sort().find((ymd) => ymd.startsWith(monthPrefix)) ?? null;
	const dayShown = chosenDay != null && days.has(chosenDay) ? chosenDay : firstOpenDay;
	const times = dayShown != null ? (days.get(dayShown) ?? []) : [];

	useEffect(() => {
		setChosenDay(null);
	}, [year, month]);

	// Late in a month the rest of it can be closed. The first thing a guest sees should be
	// a day they can pick, so an empty opening month moves on by itself, a few months at most.
	useEffect(() => {
		if (starts == null || autoMoves.current >= MAX_AUTO_MOVES) {
			return;
		}

		if ([...days.keys()].some((ymd) => ymd.startsWith(monthPrefix))) {
			autoMoves.current = MAX_AUTO_MOVES;
		} else {
			autoMoves.current += 1;
			moveMonth(1);
		}
	});

	const moveMonth = (change: number): void => {
		const next = new Date(Date.UTC(year, month - 1 + change, 1));

		setYear(next.getUTCFullYear());
		setMonth(next.getUTCMonth() + 1);
	};

	const atCurrentMonth = monthPrefix <= today.slice(0, 7);
	const monthName = new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(
		new Date(Date.UTC(year, month - 1, 1)),
	);

	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-5 grid gap-6 md:grid-cols-[1fr_15rem]">
			<div>
				<div className="flex items-center justify-between mb-3">
					<h2 className="font-semibold text-default">
						{t(monthName)} {t(String(year))}
					</h2>
					<div className="flex gap-1">
						<button
							type="button"
							aria-label={t('Previous month')}
							disabled={atCurrentMonth}
							onClick={() => {
								moveMonth(-1);
							}}
							className="h-8 w-8 rounded-md text-brand-muted hover:bg-brand-page-bg disabled:opacity-30"
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>
						<button
							type="button"
							aria-label={t('Next month')}
							onClick={() => {
								moveMonth(1);
							}}
							className="h-8 w-8 rounded-md text-brand-muted hover:bg-brand-page-bg"
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					</div>
				</div>
				<div className="grid grid-cols-7 gap-1 text-center text-xs text-brand-muted mb-1">
					{WEEKDAYS.map((weekday) => (
						<span key={weekday}>{t(weekday)}</span>
					))}
				</div>
				<div className="grid grid-cols-7 gap-1">
					{monthWeeks(year, month)
						.flat()
						.map((cell, index) => {
							if (cell == null) {
								return <span key={`blank-${String(index)}`} />;
							}

							const open = days.has(cell.ymd);
							const isChosen = cell.ymd === dayShown;

							return (
								<button
									key={cell.ymd}
									type="button"
									disabled={!open}
									data-testid={`booking-day-${cell.ymd}`}
									onClick={() => {
										setChosenDay(cell.ymd);
									}}
									className={
										isChosen
											? 'h-10 rounded-lg text-sm font-semibold bg-portal-accent text-white'
											: open
												? `h-10 rounded-lg text-sm font-semibold text-portal-accent ${OPEN_TINT}`
												: 'h-10 rounded-lg text-sm text-brand-muted/50'
									}
								>
									{t(String(cell.day))}
								</button>
							);
						})}
				</div>
				<label className="block mt-4 text-sm text-brand-muted">
					{t('Time zone')}
					<select
						value={timezone}
						onChange={(event) => {
							onTimezoneChange(event.target.value);
						}}
						className="mt-1 block w-full rounded-lg border border-brand-border bg-brand-surface text-default px-3 py-2 text-sm"
					>
						{timezoneChoices(browserZone, hostTimezone).map((zone) => (
							<option key={zone} value={zone}>
								{t(timezoneLabel(zone))}
								{zone === browserZone ? ` (${t('your browser')})` : ''}
							</option>
						))}
					</select>
				</label>
			</div>
			<div>
				<h3 className="font-semibold text-default mb-3">
					{dayShown != null ? t(formatDay(dayShown)) : t('Times')}
				</h3>
				{isLoading ? <p className="text-sm text-brand-muted">{t('Loading times...')}</p> : null}
				{isError ? <p className="text-sm text-brand-muted">{t('Times could not be loaded. Please try again.')}</p> : null}
				{!isLoading && !isError && times.length === 0 ? (
					<p className="text-sm text-brand-muted">{t('No open times this month.')}</p>
				) : null}
				<div className="space-y-2">
					{times.map((start) => (
						<button
							key={start}
							type="button"
							data-testid="booking-time"
							onClick={() => {
								onSelect(start);
							}}
							className={
								start === selected
									? 'w-full rounded-lg border border-portal-accent bg-portal-accent text-white py-2 text-sm font-semibold'
									: `w-full rounded-lg border border-transparent text-portal-accent py-2 text-sm font-semibold ${OPEN_TINT}`
							}
						>
							{t(formatTime(start, timezone))}
						</button>
					))}
				</div>
				<p className="mt-3 text-xs text-brand-muted">{t('Shown in the time zone you chose.')}</p>
			</div>
		</div>
	);
}
